'use client';

import React from 'react';
import Link from 'next/link';
import { PlayCircle, CheckSquare, Send, FileDown, Plus, RefreshCw, ShieldCheck } from 'lucide-react';
import { useRole } from '@/context/RoleContext';

const ALL_ACTIONS = [
  {
    id: 'qa-run-payroll',
    label: 'Run Payroll — Week 18',
    description: 'Calculate gross, PAYE, NI, pension',
    icon: PlayCircle,
    href: '/payroll-details',
    variant: 'primary',
    permission: 'canRunPayroll' as const,
  },
  {
    id: 'qa-approve-timesheets',
    label: 'Approve Timesheets',
    description: '4 awaiting your approval',
    icon: CheckSquare,
    href: '/time-clock',
    variant: 'warning',
    permission: 'canApproveTimesheets' as const,
  },
  {
    id: 'qa-submit-rti',
    label: 'Submit RTI to HMRC',
    description: 'FPS Week 18 — due today',
    icon: Send,
    href: '/payroll-details',
    variant: 'danger',
    permission: 'canSubmitRTI' as const,
  },
  {
    id: 'qa-download-payslips',
    label: 'Download Payslips',
    description: 'Week 17 · 47 payslips ready',
    icon: FileDown,
    href: '/payroll-details',
    variant: 'default',
    permission: 'canDownloadPayslips' as const,
  },
  {
    id: 'qa-add-worker',
    label: 'Add New Worker',
    description: 'Set up PAYE and NI details',
    icon: Plus,
    href: '/workers',
    variant: 'default',
    permission: 'canAccessWorkers' as const,
  },
  {
    id: 'qa-recalculate',
    label: 'Recalculate Deductions',
    description: 'Refresh with latest tax codes',
    icon: RefreshCw,
    href: '/payroll-details',
    variant: 'default',
    permission: 'canRunPayroll' as const,
  },
  {
    id: 'qa-cqc',
    label: 'CQC Compliance Review',
    description: 'Evidence portfolio — inspection window open',
    icon: ShieldCheck,
    href: '/compliance',
    variant: 'default',
    permission: 'canAccessCompliance' as const,
  },
];

const VARIANT_STYLES = {
  primary: 'border-primary/20 bg-primary/5 hover:bg-primary/10',
  warning: 'border-warning/20 bg-warning/5 hover:bg-warning/10',
  danger: 'border-danger/20 bg-danger/5 hover:bg-danger/10',
  default: 'border-border bg-card hover:bg-secondary',
};

const ICON_STYLES = {
  primary: 'text-primary',
  warning: 'text-warning',
  danger: 'text-danger',
  default: 'text-muted-foreground',
};

export default function QuickActions() {
  const { permissions } = useRole();
  const actions = ALL_ACTIONS.filter((a) => permissions[a.permission]);

  if (actions.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <h3 className="text-[14px] font-600 text-foreground mb-4">Quick Actions</h3>
      <ul className="space-y-2">
        {actions.map((action) => (
          <li key={action.id}>
            <Link
              href={action.href}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all duration-150 group ${VARIANT_STYLES[action.variant as keyof typeof VARIANT_STYLES]}`}
            >
              <action.icon size={16} className={`shrink-0 ${ICON_STYLES[action.variant as keyof typeof ICON_STYLES]}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-600 text-foreground leading-none mb-0.5">{action.label}</p>
                <p className="text-[11px] text-muted-foreground truncate">{action.description}</p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}